// Normalized model for the /saved library. Two Supabase tables feed one grid: builder saves
// (`saved_elements`, carrying a `metadata` JSONB + optional `thumbnail_url`) and reference
// bookmarks (`favorites`, items/blocks/recipes, NO metadata column). This module flattens both
// into a single `LibraryItem` shape and owns the per-type presentation registry.
//
// Folders / tags / pinned live INSIDE `saved_elements.metadata` (no column migration), so they
// exist only for `source: 'element'` items; favorites carry `folder: null, tags: []` in v1.

import type { SavedElement } from '@/app/hooks/useSavedElements';
import { getDefaultVersion, isVersionAtLeast } from '@/app/lib/versions';
import { routeForTextToolHash } from '@/app/lib/minecraft/textToolsShare';
import { routeForHash } from '@/app/lib/minecraft/shapeExporter';

/** The version the app opens by default. A FALLBACK only — every surface that says "you're
 *  working in X" must pass the user's LIVE selection (`useVersion().version`) instead, or it tells
 *  a user who has chosen 1.20.1 that their 1.20.1 build is older than their 26.2 and offers to
 *  open it at a version they are not using. */
export const DEFAULT_WORKING_VERSION = getDefaultVersion('java');

/** A saved build predates the version the user is working in (proper MC compare, not string). */
export function isOlderVersion(v: string | null, working: string = DEFAULT_WORKING_VERSION): boolean {
  return !!v && !isVersionAtLeast(v, working);
}

/** Pull the mod context (if any) a build was saved under. */
function readMods(meta: Record<string, unknown>): { modpack: string | null; individualMods: string[] } | null {
  const m = meta.mods as { modpack?: unknown; individualMods?: unknown } | undefined;
  if (!m || typeof m !== 'object') return null;
  const modpack = typeof m.modpack === 'string' ? m.modpack : null;
  const individualMods = Array.isArray(m.individualMods) ? m.individualMods.filter((x): x is string => typeof x === 'string') : [];
  return modpack || individualMods.length ? { modpack, individualMods } : null;
}

/** Build a tool URL that carries the version AND mod context as one-time overrides ahead of any
 *  `#stateHash`. VersionContext reads `version` / `modpack` / `mods` on mount (see its URL-param
 *  handling) without persisting them, so a build reopens exactly as it was saved. */
function withContext(openPath: string, version: string, mods: { modpack: string | null; individualMods: string[] } | null): string {
  const [base, hash] = openPath.split('#');
  const params = new URLSearchParams();
  params.set('version', version);
  if (mods?.modpack) params.set('modpack', mods.modpack);
  if (mods?.individualMods.length) params.set('mods', mods.individualMods.join(','));
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}${params.toString()}${hash ? `#${hash}` : ''}`;
}

/** Insert only a `?version=` override (mods carried alongside when present). Kept for callers that
 *  open a build at a specific version (the modal's dual "open at version" buttons). */
export function withVersion(openPath: string, version: string): string {
  return withContext(openPath, version, null);
}

/** The href to open a build at a specific version WITH its saved mod context. */
export function openHrefAtVersion(item: LibraryItem, version: string): string {
  return item.source === 'element' ? withContext(item.openPath, version, readMods(item.metadata)) : item.openPath;
}

/** The href to load a build faithfully: element builds open at their SAVED version + mods; reference
 *  bookmarks (favorites/skins/profiles, no version) open as-is. */
export function openHref(item: LibraryItem): string {
  return item.source === 'element' && item.version ? openHrefAtVersion(item, item.version) : item.openPath;
}

/** The side effect an open needs beyond the href. A non-player skin carries its texture in
 *  `metadata`, not in a URL, and the Skin Editor reads it from sessionStorage — without this the
 *  editor opens EMPTY, which looks exactly like a lost save. Every surface that links a build must
 *  call this, which is why it lives here rather than in one page's handler. */
export function prepareOpen(item: LibraryItem): void {
  if (item.type === 'skin' && !item.metadata.skinUrl && typeof item.metadata.textureBase64 === 'string') {
    try { sessionStorage.setItem('skinEditor_loadBase64', item.metadata.textureBase64); } catch { /* ignore */ }
  }
}

export interface Favorite {
  id: number;
  item_id: string;
  item_type: string;
  display_name: string;
  game_version: string;
  created_at: string;
}

/** The normalized type axis. `banner` splits into banner/shield by metadata.mode; favorites
 *  split into favorites (items/blocks/…) and recipes. Everything else mirrors the saved type. */
export type LibraryType =
  | 'favorites' | 'recipes' | 'profile' | 'skin'
  | 'banner' | 'shield' | 'armor' | 'potion' | 'firework' | 'sign' | 'book' | 'armor_stand' | 'mob_builder' | 'enchantment'
  | 'command' | 'execute' | 'tellraw' | 'title' | 'motd' | 'color_code' | 'loot_table'
  | 'pixel_shape';

export type LibraryGroup = 'Reference' | 'Design' | 'Commands' | 'Shapes';
export const GROUPS: LibraryGroup[] = ['Reference', 'Design', 'Commands', 'Shapes'];

// Folder names come from unrestricted window.prompt input, but the UI reserves a couple of tokens
// as sentinels (the Unfiled pseudo-folder, the "new folder" select option). A real folder named
// one of these would silently hijack that control, so folder creation/rename must reject them.
const RESERVED_FOLDER_NAMES = new Set(['__unfiled', '__new__']);
export function isReservedFolderName(name: string): boolean {
  return RESERVED_FOLDER_NAMES.has(name);
}

/** Length caps, enforced by CHECKs on saved_elements.metadata (migration 20261024010000_library_org_limits).
 *  Counted in code points, as Postgres char_length counts them — an emoji is one character, not two. */
export const FOLDER_NAME_MAX = 40;
export const TAG_MAX = 32;
const charCount = (s: string) => [...s].length;
/** Why a folder name can't be used, or null. */
export function folderNameProblem(name: string): string | null {
  if (isReservedFolderName(name)) return 'That folder name is reserved — pick another';
  if (charCount(name) > FOLDER_NAME_MAX) return `Folder names can be up to ${FOLDER_NAME_MAX} characters`;
  return null;
}
/** Why a tag can't be used, or null. */
export function tagProblem(tag: string): string | null {
  return charCount(tag) > TAG_MAX ? `Tags can be up to ${TAG_MAX} characters` : null;
}

interface TypeDef {
  label: string;
  group: LibraryGroup;
  /** hex for swatches / thumbnail gradients */
  hex: string;
  /** full, static Tailwind class strings (JIT-safe — never build these by interpolation) */
  tint: string;
}

// Colors mirror the live per-category accents. Tint strings are literal so Tailwind's scanner
// keeps them; do NOT template `bg-${x}-600/20` — that class would be purged.
const AMBER = 'bg-amber-600/20 hover:bg-amber-600/30 text-amber-400';
const BLUE = 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-400';
const PURPLE = 'bg-purple-600/20 hover:bg-purple-600/30 text-purple-400';
const RED = 'bg-red-600/20 hover:bg-red-600/30 text-red-400';
const CYAN = 'bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400';
const EMERALD = 'bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400';

export const TYPE_META: Record<LibraryType, TypeDef> = {
  favorites: { label: 'Items & Blocks', group: 'Reference', hex: '#fbbf24', tint: AMBER },
  recipes: { label: 'Recipes', group: 'Reference', hex: '#fbbf24', tint: AMBER },
  profile: { label: 'Profiles', group: 'Reference', hex: '#22d3ee', tint: CYAN },
  skin: { label: 'Skins', group: 'Reference', hex: '#22d3ee', tint: CYAN },
  banner: { label: 'Banners', group: 'Design', hex: '#f59e0b', tint: AMBER },
  shield: { label: 'Shields', group: 'Design', hex: '#60a5fa', tint: BLUE },
  armor: { label: 'Armor', group: 'Design', hex: '#a78bfa', tint: PURPLE },
  potion: { label: 'Potions', group: 'Design', hex: '#a78bfa', tint: PURPLE },
  firework: { label: 'Fireworks', group: 'Design', hex: '#f87171', tint: RED },
  sign: { label: 'Signs', group: 'Design', hex: '#f59e0b', tint: AMBER },
  book: { label: 'Books', group: 'Design', hex: '#34d399', tint: EMERALD },
  armor_stand: { label: 'Armor Stands', group: 'Design', hex: '#34d399', tint: EMERALD },
  mob_builder: { label: 'Mobs', group: 'Design', hex: '#f87171', tint: RED },
  enchantment: { label: 'Enchantments', group: 'Design', hex: '#a78bfa', tint: PURPLE },
  command: { label: 'Commands', group: 'Commands', hex: '#34d399', tint: EMERALD },
  execute: { label: 'Execute Chains', group: 'Commands', hex: '#34d399', tint: EMERALD },
  tellraw: { label: 'Tellraws', group: 'Commands', hex: '#34d399', tint: EMERALD },
  title: { label: 'Titles', group: 'Commands', hex: '#f59e0b', tint: AMBER },
  motd: { label: 'MOTDs', group: 'Commands', hex: '#22d3ee', tint: CYAN },
  color_code: { label: 'Colored Text', group: 'Commands', hex: '#f472b6', tint: PURPLE },
  loot_table: { label: 'Loot Tables', group: 'Commands', hex: '#34d399', tint: EMERALD },
  pixel_shape: { label: 'Pixel Shapes', group: 'Shapes', hex: '#34d399', tint: EMERALD },
};

/** Singular form for the type badge on a card ("Banners" -> "Banner"). */
export function badgeLabel(type: LibraryType): string {
  const l = TYPE_META[type].label;
  return l === 'Items & Blocks' ? 'Item' : l.replace(/s$/, '').replace(/ Chain$/, '');
}

export interface LibraryItem {
  uid: string;                        // stable, unique across both sources
  // Origin table. Also the seam for a future community feature: a shared design would be a third
  // source (or an element with metadata.visibility='public') slotting into the same grid/registry.
  source: 'element' | 'favorite';
  id: number;                         // row id within its own table
  type: LibraryType;
  name: string;
  version: string | null;            // null => shown as "—"
  createdAt: string;
  folder: string | null;             // element-only in v1
  tags: string[];                    // element-only in v1
  pinned: boolean;
  openPath: string;                  // tool URL to load this build
  buttonLabel: string;
  summary: string;
  metadata: Record<string, unknown>; // raw, for thumbnail rendering + skin session-load
}

// Per-type tool route + open-button label, matching the current page exactly.
const OPEN: Partial<Record<LibraryType, { path: (hash: string) => string; label: string }>> = {
  armor: { path: h => `/tools/armor-editor#${h}`, label: 'Load in Builder' },
  banner: { path: h => `/tools/banner-editor#${h}`, label: 'Load in Editor' },
  shield: { path: h => `/tools/shield-editor#${h}`, label: 'Load in Editor' },
  potion: { path: h => `/tools/potion-editor#${h}`, label: 'Load in Editor' },
  sign: { path: h => `/tools/sign-generator#${h}`, label: 'Load in Editor' },
  book: { path: h => `/tools/book-editor#${h}`, label: 'Load in Editor' },
  firework: { path: h => `/tools/firework-editor#${h}`, label: 'Load in Editor' },
  armor_stand: { path: h => `/tools/armor-stand#${h}`, label: 'Load in Editor' },
  mob_builder: { path: h => `/tools/mob-builder#${h}`, label: 'Load in Builder' },
  tellraw: { path: h => `/tools/tellraw-editor#${h}`, label: 'Load in Editor' },
  command: { path: h => `/tools/command-generator#${h}`, label: 'Load in Generator' },
  execute: { path: h => `/tools/execute-builder#${h}`, label: 'Load in Builder' },
  title: { path: h => `/tools/title-generator#${h}`, label: 'Load in Generator' },
  // Open on the saved shape's own page: a sphere opened on the circle hub stranded its buttons.
  pixel_shape: { path: h => `${routeForHash(h)}#${h}`, label: 'Load in Generator' },
  motd: { path: h => `/tools/motd-generator#${h}`, label: 'Load in Generator' },
  color_code: { path: h => `${routeForTextToolHash(h)}#${h}`, label: 'Load in Generator' },
  loot_table: { path: h => `/tools/loot-table-builder#${h}`, label: 'Load in Builder' },
};
// enchantment (enchantment-builder) is handled directly in elementOpen below, not via this map,
// because its route differs from the generic `#hash` pattern only in tool slug.

function orgFields(meta: Record<string, unknown>): { folder: string | null; tags: string[]; pinned: boolean } {
  const folder = typeof meta.folder === 'string' ? meta.folder : null;
  const tags = Array.isArray(meta.tags) ? meta.tags.filter((t): t is string => typeof t === 'string') : [];
  const pinned = meta.pinned === true;
  return { folder, tags, pinned };
}

/** Map a raw `saved_elements` row to its normalized library type (handling banner/shield split). */
function elementType(el: SavedElement): LibraryType | null {
  if (el.type === 'banner') {
    const mode = (el.metadata as { mode?: string }).mode;
    return mode === 'shield' ? 'shield' : 'banner';
  }
  // The remaining saved types share their name with a LibraryType (enchantment included).
  return (el.type in TYPE_META ? (el.type as LibraryType) : null);
}

function elementOpen(el: SavedElement): { path: string; label: string } {
  const meta = el.metadata as Record<string, unknown>;
  const hash = typeof meta.stateHash === 'string' ? meta.stateHash : '';

  if (el.type === 'enchantment') return { path: `/tools/enchantment-builder#${hash}`, label: 'Load in Builder' };

  if (el.type === 'skin') {
    const skinUrl = typeof meta.skinUrl === 'string' ? meta.skinUrl : '';
    const model = typeof meta.modelType === 'string' ? meta.modelType : 'classic';
    const path = skinUrl
      ? `/tools/skin-editor?skinUrl=${encodeURIComponent(skinUrl)}&model=${model}`
      : `/tools/skin-editor?model=${model}`;
    return { path, label: 'Open in Editor' };
  }

  if (el.type === 'profile') {
    const username = typeof meta.username === 'string' ? meta.username : '';
    return { path: `/tools/uuid-lookup?username=${encodeURIComponent(username)}`, label: 'View Profile' };
  }

  const lt = elementType(el);
  const def = lt && OPEN[lt];
  if (def) return { path: def.path(hash), label: def.label };
  return { path: '/saved', label: 'Open' };
}

/** Flatten both Supabase sources into one normalized, presentation-ready list. */
export function toLibraryItems(elements: SavedElement[], favorites: Favorite[]): LibraryItem[] {
  const out: LibraryItem[] = [];

  for (const el of elements) {
    const type = elementType(el);
    if (!type) continue;
    const meta = el.metadata as Record<string, unknown>;
    const { folder, tags, pinned } = orgFields(meta);
    const open = elementOpen(el);
    out.push({
      uid: `element-${el.id}`,
      source: 'element',
      id: el.id,
      type,
      name: el.display_name,
      // 'n/a' is what the version-independent tools (MOTD Generator, Pixel Shapes) save. It is "no version",
      // not a version: read as one it rendered an amber "n/a — older than your 26.2" chip and opened the
      // tool with ?version=n/a.
      version: typeof meta.version === 'string' && meta.version !== 'n/a' ? meta.version : null,
      createdAt: el.created_at,
      folder,
      tags,
      pinned,
      openPath: open.path,
      buttonLabel: open.label,
      summary: typeof meta.summary === 'string' ? meta.summary : '',
      metadata: meta,
    });
  }

  for (const fav of favorites) {
    const isRecipe = fav.item_type === 'recipe';
    out.push({
      uid: `favorite-${fav.id}`,
      source: 'favorite',
      id: fav.id,
      type: isRecipe ? 'recipes' : 'favorites',
      name: fav.display_name,
      version: fav.game_version || null,
      createdAt: fav.created_at,
      folder: null,
      tags: [],
      pinned: false,
      openPath: isRecipe
        ? `/tools/recipe-viewer?item=${encodeURIComponent(fav.item_id.includes(':') ? fav.item_id.split(':')[1] : fav.item_id)}`
        : `/tools/id-list?search=${encodeURIComponent(fav.display_name)}`,
      buttonLabel: isRecipe ? 'View in Recipe Viewer' : 'View in ID List',
      summary: fav.item_id,
      // keep the raw favorite fields so the card can un-favourite without another lookup
      metadata: { item_id: fav.item_id, item_type: fav.item_type, game_version: fav.game_version },
    });
  }

  return out;
}
